import type { RSVP } from '@/types';

export type GuestSeatColours = {
  fill: string;
  stroke: string;
  secondaryFill?: string;
  secondaryStroke?: string;
};

const GOLDEN_ANGLE = 137.508;

/** Firestore `colourSchemeVersion` when `rootHueById` stores palette indices (not HSL hue degrees). */
export const RELATIONSHIP_PARTY_COLOUR_SCHEME_VERSION = 2;

/**
 * Distinct standard colours for relationship groups (fill + stroke pairs).
 * Order matters for stable assignment when there are more groups than colours (indices wrap).
 */
export const RELATIONSHIP_PARTY_PALETTE: readonly GuestSeatColours[] = [
  { fill: '#ef4444', stroke: '#b91c1c' },
  { fill: '#3b82f6', stroke: '#1d4ed8' },
  { fill: '#22c55e', stroke: '#166534' },
  { fill: '#f97316', stroke: '#c2410c' },
  { fill: '#eab308', stroke: '#a16207' },
  { fill: '#14b8a6', stroke: '#0f766e' },
  { fill: '#d946ef', stroke: '#86198f' },
  { fill: '#06b6d4', stroke: '#0e7490' },
  { fill: '#8b5cf6', stroke: '#5b21b6' },
  { fill: '#ec4899', stroke: '#9d174d' },
  { fill: '#84cc16', stroke: '#3f6212' },
  { fill: '#6366f1', stroke: '#3730a3' },
  { fill: '#f43f5e', stroke: '#9f1239' },
  { fill: '#0ea5e9', stroke: '#0369a1' },
  { fill: '#f59e0b', stroke: '#b45309' },
  { fill: '#a855f7', stroke: '#6b21a8' },
] as const;

const SOLO_GUEST_COLOURS: GuestSeatColours = {
  fill: '#d1d5db',
  stroke: '#6b7280',
};

function hslToHex(h: number, s: number, l: number): string {
  const s1 = s / 100;
  const l1 = l / 100;
  const a = s1 * Math.min(l1, 1 - l1);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = l1 - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c);
  };
  const r = f(0);
  const g = f(8);
  const b = f(4);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

function resolveInviterGuest(invitedBy: string, guests: RSVP[]): RSVP | undefined {
  const t = invitedBy.trim();
  if (!t) {
    return undefined;
  }
  return guests.find((g) => g.id === t || (typeof g.userId === 'string' && g.userId === t));
}

function getPrimaryGroupKey(guest: RSVP, guests: RSVP[]): string {
  const ib =
    typeof guest.invitedBy === 'string' ? guest.invitedBy.trim() : '';
  if (!ib) {
    return guest.id;
  }
  const inviter = resolveInviterGuest(ib, guests);
  return inviter?.id ?? guest.id;
}

function hashStringToHue(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash % 360;
}

export function collectGuestRelationshipRoots(guests: RSVP[]): string[] {
  const hasInvitees = new Set<string>();
  for (const g of guests) {
    const ib =
      typeof g.invitedBy === 'string' ? g.invitedBy.trim() : '';
    if (!ib) {
      continue;
    }
    const inviter = resolveInviterGuest(ib, guests);
    if (inviter) {
      hasInvitees.add(inviter.id);
    }
  }

  const roots = new Set<string>();
  for (const g of guests) {
    roots.add(getPrimaryGroupKey(g, guests));
    if (hasInvitees.has(g.id)) {
      roots.add(g.id);
    }
  }

  return [...roots].sort();
}

function clampPaletteIndex(value: number, paletteLength: number): number {
  if (!Number.isFinite(value) || paletteLength <= 0) {
    return 0;
  }
  const i = Math.floor(value);
  return ((i % paletteLength) + paletteLength) % paletteLength;
}

function partyColoursAtIndex(index: number): GuestSeatColours {
  const palette = RELATIONSHIP_PARTY_PALETTE;
  const slot = clampPaletteIndex(index, palette.length);
  return palette[slot]!;
}

/**
 * Maps RSVP ids to seat colours.
 * - Primary colour: immediate inviter group (A invites B => A and B share A's colour).
 * - Secondary colour: for bridge guests who were invited by someone and also invite others.
 * @param rootPaletteIndexByRootId Optional stable indices into {@link RELATIONSHIP_PARTY_PALETTE} per relationship root id.
 */
export function buildGuestPartyColourMap(
  guests: RSVP[],
  rootPaletteIndexByRootId?: Map<string, number>
): Map<string, GuestSeatColours> {
  const paletteLength = RELATIONSHIP_PARTY_PALETTE.length;
  const hasInvitees = new Set<string>();
  for (const g of guests) {
    const ib =
      typeof g.invitedBy === 'string' ? g.invitedBy.trim() : '';
    if (!ib) {
      continue;
    }
    const inviter = resolveInviterGuest(ib, guests);
    if (inviter) {
      hasInvitees.add(inviter.id);
    }
  }

  const sortedRoots = collectGuestRelationshipRoots(guests);
  const rootToPaletteIndex = new Map<string, number>();
  sortedRoots.forEach((root, i) => {
    const explicit = rootPaletteIndexByRootId?.get(root);
    if (typeof explicit === 'number' && Number.isFinite(explicit)) {
      rootToPaletteIndex.set(root, clampPaletteIndex(explicit, paletteLength));
      return;
    }
    rootToPaletteIndex.set(
      root,
      (hashStringToHue(root) + i) % paletteLength
    );
  });

  const result = new Map<string, GuestSeatColours>();
  for (const g of guests) {
    const hasInviter =
      typeof g.invitedBy === 'string' && g.invitedBy.trim().length > 0;
    const isInviter = hasInvitees.has(g.id);
    if (!hasInviter && !isInviter) {
      result.set(g.id, {
        fill: SOLO_GUEST_COLOURS.fill,
        stroke: SOLO_GUEST_COLOURS.stroke,
      });
      continue;
    }

    const root = getPrimaryGroupKey(g, guests);
    const primaryIdx = rootToPaletteIndex.get(root) ?? 0;
    const base = partyColoursAtIndex(primaryIdx);
    const seatColours: GuestSeatColours = {
      fill: base.fill,
      stroke: base.stroke,
    };
    if (hasInvitees.has(g.id) && root !== g.id) {
      const secondaryIdx = rootToPaletteIndex.get(g.id) ?? primaryIdx;
      const secondary = partyColoursAtIndex(secondaryIdx);
      seatColours.secondaryFill = secondary.fill;
      seatColours.secondaryStroke = secondary.stroke;
    }
    result.set(g.id, seatColours);
  }
  return result;
}

export const DEFAULT_OCCUPIED_SEAT_COLOURS: GuestSeatColours = {
  fill: '#10b981',
  stroke: '#059669',
};

/** Occupied seats when relationship party colours are hidden (matches default empty chair tone). */
export const NEUTRAL_RELATIONSHIPS_OFF_SEAT_COLOURS: GuestSeatColours = {
  fill: '#e5e7eb',
  stroke: '#9ca3af',
};

export type PrefixBorderEntry = {
  borderColour: string;
  label: string;
};

/**
 * Builds a stable colour for each unique guest prefix (rank field).
 * Returns two maps:
 *  - byRsvpId: rsvpId -> border hex colour (for seat rendering)
 *  - legend: sorted prefix label -> border hex colour (for the UI legend)
 */
export function buildPrefixBorderColourMap(
  guests: RSVP[]
): {
  byRsvpId: Map<string, string>;
  legend: PrefixBorderEntry[];
} {
  const prefixSet = new Map<string, string>();
  const guestPrefix = new Map<string, string>();

  for (const g of guests) {
    const raw = typeof g.rank === 'string' ? g.rank.trim() : '';
    if (!raw) continue;
    const key = raw.toLowerCase();
    if (!prefixSet.has(key)) {
      prefixSet.set(key, raw);
    }
    guestPrefix.set(g.id, key);
  }

  const sorted = [...prefixSet.keys()].sort();
  const prefixToColour = new Map<string, string>();
  sorted.forEach((key, i) => {
    const hue = (i * GOLDEN_ANGLE + 30) % 360;
    prefixToColour.set(key, hslToHex(hue, 80, 40));
  });

  const byRsvpId = new Map<string, string>();
  for (const g of guests) {
    const key = guestPrefix.get(g.id);
    if (key) {
      byRsvpId.set(g.id, prefixToColour.get(key) ?? '#6b7280');
    }
  }

  const legend: PrefixBorderEntry[] = sorted.map((key) => ({
    label: prefixSet.get(key) ?? key,
    borderColour: prefixToColour.get(key) ?? '#6b7280',
  }));

  return { byRsvpId, legend };
}
